import { InputHTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import { ClearButton, ClearSelection, FilterStyledPopper, TextInput } from '@fabric-ui/components';
import { ArrowDown, Calendar, CalendarFilled } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { emptyObject, iconSize, noop } from '@fabric-ui/utils';
import { Box, Button, ClickAwayListener, Divider, IconButton, SvgIcon, useTheme } from '@mui/material';
import { isEqual, isValid, parse } from 'date-fns';
import { epochDateString, RelativeDatePeriod, RelativeDateType } from './AvDateRangePicker.constants';
import { CalenderPicker, DynamicRangeSelect, NotSetOption, RelativeDateOptions } from './PickerComponents';
import { ShortVal } from './ShortVal';
import { DateRangeValueType, PredefinedPreset, Preset, ValueWithPreset } from './types';
import {
  customPreset,
  dateFormat,
  formatDate,
  getDatesFromDefinition,
  getEndOfDay,
  getInputValue,
  getStartOfDay,
  isDateRangeEmpty,
  isDateRangeNotEmpty,
  isDateRangeValue,
  isPresetValid,
  RadioButtonValue,
  RelativeUnit,
  validateValue,
} from './utils';
import 'react-day-picker/dist/style.css';

export interface DateRangePickerProps {
  value?: DateRangeValueType | null;
  activePresetValue?: Preset;
  predefinedPresets?: PredefinedPreset[];
  onChange: (data: ValueWithPreset) => void;
  onClose?: () => void;
  isRange?: boolean;
  showRelativeOptions?: boolean;
  applyOnChange?: boolean;
  showAsCalendar?: boolean;
  isEditableInput?: boolean;
  showInputIcon?: boolean;
  inputSx?: any;
  timeOptions?: any[];
  acceptNullDate?: boolean;
  showDropdownIcon?: boolean;
  allowPastDatesOnDayPicker?: boolean;
  allowFutureDates?: boolean;
  isFilterExpression?: boolean;
  startAdornment?: () => ReactNode;
  icon?: ReactNode;
  showOptions?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  defaultRadioValue?: RadioButtonValue;
  showIntervalShortVal?: boolean;
  customFormat?: string;
  showMinutesAndHours?: boolean;
  isDateValueControlled?: boolean;
  showClearIcon?: boolean;
}

export function AvDateRangePicker({
  value: _value,
  activePresetValue,
  onChange,
  onClose = noop,
  isRange = false,
  showRelativeOptions = false,
  predefinedPresets,
  applyOnChange = false,
  showAsCalendar = false,
  isEditableInput = true,
  showInputIcon = true,
  inputSx = emptyObject,
  timeOptions,
  acceptNullDate = true,
  showDropdownIcon = false,
  allowPastDatesOnDayPicker = true,
  allowFutureDates = true,
  isFilterExpression = false,
  startAdornment,
  icon,
  showOptions = true,
  inputProps = emptyObject,
  defaultRadioValue = undefined,
  showIntervalShortVal = false,
  customFormat = '',
  showMinutesAndHours = false,
  isDateValueControlled = false,
  showClearIcon = false,
}: DateRangePickerProps) {
  const value = _value === null ? 'empty' : _value;

  const theme = useTheme();
  const [preset, setPreset] = useState<Preset | undefined>(() => {
    if (activePresetValue) {
      return activePresetValue;
    }
    const validated = validateValue(value, isRange);
    return isEqual(isDateRangeValue(validated) ? validated.from : 0, new Date(epochDateString))
      ? { type: RelativeDateType.before }
      : undefined;
  });

  const [radioValue, setRadioValue] = useState(
    defaultRadioValue ??
      (value === 'empty' ? RadioButtonValue.empty : value === 'notEmpty' ? RadioButtonValue.notEmpty : RadioButtonValue.relative)
  );
  const [isPreset, setIsPreset] = useState(!!activePresetValue);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<DateRangeValueType>(() =>
    activePresetValue ? { type: 'date', ...getDatesFromDefinition(preset) } : validateValue(value, isRange)
  );

  useEffect(() => {
    if (isDateValueControlled) {
      setDate(activePresetValue ? getDatesFromDefinition(preset) : validateValue(value, isRange));
    }
  }, [value]);

  const [inputFocus, setInputFocus] = useState(false);
  const [isInputError, setIsInputError] = useState(false);
  const [editableInputValue, setEditableInputValue] = useState(getInputValue({ date, isRange, timeOptions, preset }));
  const [month, setMonth] = useState(isRange && isDateRangeValue(date) ? (date?.to ?? date?.from ?? null) : null);
  const anchorEl = useRef<any>(undefined);

  useEffect(() => {
    const shouldSyncValueToPreset =
      isRange &&
      isDateRangeValue(date) &&
      isDateRangeValue(value) &&
      showRelativeOptions &&
      activePresetValue &&
      !isEqual(new Date(value.from), date.from);
    if (shouldSyncValueToPreset) {
      onChange({ value: date, preset });
    }
  }, []);

  const handleClose = (revertChanges = true) => {
    onClose();
    setOpen(false);
    if (revertChanges) {
      setDate(value);
      setPreset(activePresetValue);
    }
  };

  const onSelect = (selected, isFromPicker = false, newPreset = preset) => {
    const isMinutesOrHours = newPreset?.period && [RelativeDatePeriod.hours, RelativeDatePeriod.minutes].includes(newPreset.period);
    if (isDateRangeEmpty(selected) || isDateRangeNotEmpty(selected)) {
      setDate(selected);
      if (applyOnChange) {
        onChange({ value: selected, preset: undefined });
        setOpen(false);
      }
      return;
    }

    const updateValue =
      selected === undefined
        ? undefined
        : isRange
          ? {
              from:
                newPreset?.type === RelativeDateType.before
                  ? new Date(epochDateString)
                  : isMinutesOrHours
                    ? selected.from
                    : getStartOfDay(selected.from),
              to: selected?.to
                ? isMinutesOrHours
                  ? selected.to
                  : getEndOfDay(selected.to)
                : isRange
                  ? getEndOfDay(selected.from)
                  : undefined,
            }
          : { from: isDateRangeValue(selected) ? selected.from : selected };
    setDate(updateValue);
    setIsPreset(!isFromPicker);
    setEditableInputValue(getInputValue({ date: updateValue, isRange, timeOptions, preset: newPreset }));
    setIsInputError(false);
    setInputFocus(false);
    if (isFromPicker) {
      setPreset(undefined);
    }
    if (applyOnChange) {
      const finalPreset = !isFromPicker && (isEmpty || isNotEmpty || !newPreset) ? undefined : newPreset;
      const finalValue =
        isFilterExpression && finalPreset && finalPreset
          ? ({
              relative: {
                unit: finalPreset.period === 'days' ? 'DAYS' : 'MONTHS',
                value: finalPreset.count,
                direction: finalPreset.type,
              },
            } as any)
          : updateValue;

      onChange({ value: finalValue, preset: finalPreset });
      setOpen(false);
    }
  };

  const validateInput = () => {
    const inputDates = editableInputValue
      ? editableInputValue.split('-').map(date => {
          const parsedDate = parse(date.trim(), dateFormat, new Date());
          return isValid(parsedDate) ? formatDate(parsedDate, dateFormat) : null;
        })
      : undefined;
    const filteredValues = inputDates ? inputDates.reduce<string[]>((acc, i) => (i ? [...acc, i] : acc), []) : [];
    if (filteredValues.length === inputDates?.length) {
      const sortedValues = filteredValues.sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateA.getTime() - dateB.getTime();
      });
      onSelect(
        isRange
          ? preset?.type === RelativeDateType.before
            ? undefined
            : {
                from: new Date(sortedValues[0]),
                to: getEndOfDay(new Date(sortedValues[1])),
              }
          : { from: new Date(filteredValues[0]) },
        true
      );
      setEditableInputValue(sortedValues.join(' - '));
      setIsInputError(false);
      setIsPreset(false);
    } else {
      setIsInputError(true);
    }
  };

  const updatePreset = (key, value) => {
    const next = { ...preset, [key]: value };
    setPreset(next);
    const date = getDatesFromDefinition(next);
    onSelect(date, undefined, next);
  };

  const onPresetClick = p => {
    if (p.title === customPreset) {
      onSelect(undefined);
      setPreset({});
      return;
    }
    setPreset(p.definition);
    const date = getDatesFromDefinition(p.definition);
    onSelect(date, undefined, p.definition);
    setMonth(date.from);
  };

  const isEmpty = radioValue === RadioButtonValue.empty;
  const isNotEmpty = radioValue === RadioButtonValue.notEmpty;
  const isRelativeOptions = !isNotEmpty && !isEmpty && showRelativeOptions;
  const inputError = isInputError ? `format is ${dateFormat}` : '';

  const onSetRadioButton = v => {
    setRadioValue(v);
    if (v === RadioButtonValue.empty) {
      onSelect('empty');
    } else if (v === RadioButtonValue.notEmpty) {
      onSelect('notEmpty');
    } else {
      onSelect(validateValue(value, isRange));
      setPreset(activePresetValue);
    }
  };

  const onClearSelection = () => {
    onSelect(undefined);
    setPreset(undefined);
    if (radioValue === RadioButtonValue.empty) {
      setRadioValue(RadioButtonValue.relative);
    }
  };

  const picker = (
    <Box sx={{ backgroundColor: 'var(--color-surface-base-primary)' }}>
      {showRelativeOptions && showOptions && (
        <>
          <NotSetOption value={radioValue} onChange={onSetRadioButton} />
          <Divider />
        </>
      )}
      <Box sx={{ ...flex.row, ...rdpStyles(isRange) }}>
        {isRelativeOptions && (
          <>
            <RelativeDateOptions
              allowFutureDates={allowFutureDates}
              onPresetClick={onPresetClick}
              isNull={isEmpty}
              isPreset={isPreset}
              preset={preset}
              predefinedPresets={predefinedPresets}
            />
            <Divider flexItem orientation="vertical" sx={{ borderColor: 'var(--color-border-base-primary)' }} />
          </>
        )}
        <Box sx={{ ...flex.col, gap: 3, pt: 2, pb: isEditableInput && !applyOnChange ? 0 : 2 }}>
          {(isEmpty || isNotEmpty) && (
            <Box
              sx={{
                ...flex.center,
                width: '100%',
                px: 3,
                pb: 6,
                pt: 1,
              }}>
              <Box
                sx={{
                  bgcolor: 'var(--color-surface-base-secondary)',
                  p: 2,
                  width: 595,
                  ...flex.itemsCenter,
                  gap: 1.5,
                }}>
                <SvgIcon sx={{ fontSize: 24, color: 'var(--color-content-base-secondary)' }}>
                  {isEmpty ? <Calendar /> : <CalendarFilled />}
                </SvgIcon>
                <div>
                  Show only items that their date{' '}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {isEmpty ? 'Is Empty' : 'Is Not Empty'}
                  </Box>
                </div>
              </Box>
            </Box>
          )}

          {showRelativeOptions && (
            <DynamicRangeSelect
              isNull={isEmpty || isNotEmpty}
              disablePortal={showAsCalendar}
              predicate={({ value }) =>
                allowFutureDates ||
                RelativeDateType.last === value ||
                RelativeDateType.previous === value ||
                RelativeDateType.before === value
              }
              preset={preset}
              inputFocus={inputFocus}
              onBlur={() => setInputFocus(false)}
              updatePreset={updatePreset}
              showMinutesAndHours={showMinutesAndHours}
            />
          )}
          {!isEmpty && !isNotEmpty && (
            <CalenderPicker
              onSelect={v => {
                if (v || acceptNullDate) {
                  setPreset(undefined);
                  onSelect(v);
                }
              }}
              date={date}
              isRange={isRange}
              month={month}
              onMonthChange={setMonth}
              allowFutureDates={allowFutureDates}
              allowPastDatesOnDayPicker={allowPastDatesOnDayPicker}
              timeOptions={timeOptions}
              onChangeTimeOptions={v => {
                if (isDateRangeValue(date)) {
                  const time = v.split(':');
                  date.from.setHours(time[0]);
                  date.from.setMinutes(time[1]);
                  onSelect(date);
                }
              }}
            />
          )}
        </Box>
      </Box>
      {!applyOnChange && (
        <Box sx={{ ...flex.col }}>
          <Divider />
          <Box sx={{ ...flex.justifyBetweenCenter, pl: 2, pr: 3, py: 2 }}>
            <Box sx={{ ...flex.row, gap: 1 }}>
              <div>
                {isEditableInput && !isEmpty && !isNotEmpty && (
                  <TextInput
                    disabled={isEmpty || isNotEmpty}
                    style={{ width: 250 }}
                    size="small"
                    onBlur={validateInput}
                    onChange={setEditableInputValue}
                    value={editableInputValue}
                    error={inputError}
                    disableClearButton
                    autoComplete="off"
                    tooltipContent={inputError}
                    onKeyDown={e => e.code === 'Enter' && validateInput()}
                  />
                )}
              </div>
              <ClearSelection disabled={!date} onClick={onClearSelection} />
            </Box>
            <div>
              <Button size="small" onClick={() => handleClose()}>
                Cancel
              </Button>
              <Button
                size="small"
                disabled={
                  (!(isEmpty || isNotEmpty || date === undefined) && isRange && isDateRangeValue(date) && (!date?.from || !date.to)) ||
                  (radioValue === RadioButtonValue.relative && !isPresetValid(preset))
                }
                variant="contained"
                onClick={() => {
                  const finalPreset = !!date && !isEmpty && !isNotEmpty && isPreset && preset ? preset : undefined;
                  const finalValue: any =
                    isFilterExpression && finalPreset && finalPreset.type !== RelativeDateType.before
                      ? {
                          relative: {
                            unit: RelativeUnit[finalPreset.period?.toUpperCase() ?? ''],
                            value: finalPreset.count,
                            direction: finalPreset.type,
                          },
                        }
                      : date;

                  onChange({ value: finalValue, preset: finalPreset });
                  handleClose(false);
                }}>
                Apply
              </Button>
            </div>
          </Box>
        </Box>
      )}
    </Box>
  );

  const TextInputProps: any = {
    size: 'small',
    value:
      value === 'empty'
        ? 'Not Set'
        : `${getInputValue({ date: validateValue(date, isRange), isRange, timeOptions, preset, customFormat })}`,
  };

  const iconComponent = icon || (
    <Box sx={{ ...flex.center, gap: 1 }}>
      {showInputIcon && <Calendar />}
      {showIntervalShortVal && <ShortVal value={preset?.count} unit={preset?.period} />}
    </Box>
  );

  return showAsCalendar ? (
    picker
  ) : (
    <ClickAwayListener onClickAway={() => (open ? handleClose() : noop())}>
      <div>
        <TextInput
          {...TextInputProps}
          sx={{ width: 280, ...inputSx }}
          helperText={isRange ? 'Select Dates' : 'Select Date'}
          autoComplete="off"
          onClick={() => setOpen(true)}
          startAdornment={startAdornment}
          icon={iconComponent}
          ref={anchorEl}
          disableClearButton
          actionIcons={[
            ...(showDropdownIcon
              ? [
                  <IconButton key="dropdown-icon" sx={{ color: 'var(--color-content-interactive-secondary-default)' }}>
                    <ArrowDown style={iconSize(16)} />
                  </IconButton>,
                ]
              : []),
            ...(showClearIcon
              ? [
                  <ClearButton
                    key="clear-icon"
                    sx={{
                      opacity: 1,
                      '.MuiTextField-root:not(:hover):not(:focus-within) &': { opacity: 0 },
                      transition: theme.transitions.create(['opacity'], { duration: theme.transitions.duration.short }),
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      onSelect('empty');
                    }}
                  />,
                ]
              : []),
          ]}
          {...inputProps}
        />
        <FilterStyledPopper open={open} anchorEl={anchorEl.current} sx={{ p: 0 }} placement="bottom-start">
          {picker}
        </FilterStyledPopper>
      </div>
    </ClickAwayListener>
  );
}

export const rdpStyles = isRange => ({
  '.rdp': {
    margin: '0px 24px',
    background: 'var(--color-surface-field-default)',
    '--rdp-cell-size': '30px',
    '--rdp-accent-color': 'var(--color-surface-interactive-primary-active)',
    '--rdp-background-color': 'var(--color-surface-interactive-primary-active)',

    color: 'var(--color-content-base-primary)',
    '.rdp-day': {
      fontSize: 13,
    },
    '.rdp-head_cell': {
      color: 'var(--color-content-interactive-primary-disabled)',
      textTransform: 'capitalize',
    },
    '.rdp-table': {
      borderSpacing: '0px 3px',
      borderCollapse: 'separate',
    },
    '.rdp-day:not(.rdp-day_selected)': {
      borderRadius: '10px',
    },
    ...(!isRange && {
      '.rdp-day_selected': {
        background: 'var(--color-surface-interactive-primary-active)',
        color: 'var(--color-content-inverted-base-primary)',
        borderRadius: '10px',
      },
    }),
    '.rdp-day_outside': {
      color: 'var(--color-content-interactive-primary-disabled)',
      '&.rdp-day_range_middle': {
        background: 'var(--color-surface-interactive-primary-active)',
        color: 'var(--color-content-interactive-primary-disabled)',
        opacity: 1,
      },
    },
    '.rdp-day_range_end,.rdp-day_range_start': {
      borderRadius: '10px',
      background: 'var(--color-surface-interactive-primary-active)',
      color: 'var(--color-content-inverted-base-primary)',
    },
    '.rdp-day_range_middle, .rdp-button:hover:not([disabled]):not(.rdp-day_selected)': {
      color: 'var(--color-content-inverted-base-primary)',
    },
    '.rdp-day_today': {
      fontWeight: 'normal',
      border: '1px solid var(--color-content-base-primary)',
      borderRadius: '10px',
      '&.rdp-day_range_middle': {
        borderRadius: 0,
      },
    },
  },
});
